import { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { getInternshipEvaluation } from '../../../../utils/api/internship'
import { InternshipEvaluation } from '../../../../utils/entities/internshipEvaluation'
import { getActiveLanguage, getTranslate } from 'react-localize-redux'
import { connect } from 'react-redux'
import { InstitutionContext } from '../../../../Providers/InstitutionProvider'
import { getUser } from '../../../../reducers/UserReducer'
import EvaluationPanel from './EvaluationPanel'
import Loading from '../../../../HOC/Loading'
import { saveEvaluation } from '../../../../utils/api/evaluation'
import { displayErrorMessage, onError } from '../../../../utils/apiHelper'
import { HttpStatuses } from '../../../../utils/http'

const mapStateToProps = state => ({
  activeLanguage: getActiveLanguage(state.locale).code,
  t: getTranslate(state.locale),
  user: getUser(state.getUser)
})

const InstitutionEvaluationPanel = ({ activeLanguage, internship, user, t }) => {
  const { evaluationQuestions } = useContext(InstitutionContext)

  const [evaluation, setEvaluation] = useState(null)
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setSubmitted(false)
  }, [internship?.id])

  const language = useMemo(() => activeLanguage.split('_')[0], [activeLanguage])

  const loadEvaluation = useCallback(() => {
    if (user && internship) {
      setLoading(true)

      getInternshipEvaluation(user, internship).then(json => {
        if (json?.data) {
          setEvaluation(new InternshipEvaluation(json.data))
        }

        setLoading(false)
      })
    }
  }, [internship, user, setLoading, setEvaluation])

  const submitFeedback = (studentEvaluationAnswers, studentEvaluationComment) => {
    const questions = studentEvaluationAnswers.map(item => ({ questionId: item.id, questionType: item.questionType, answer: item.answer }))

    const dataToSend = {
      internshipId: internship.id,
      answers: questions,
      comment: studentEvaluationComment ? studentEvaluationComment.trim() : null
    }

    setLoading(true)
    saveEvaluation(user, dataToSend).then(json => {
      loadEvaluation()
      setSubmitted(true)
    }).catch(error => {
      if (error.status === HttpStatuses.FORBIDDEN) {
        displayErrorMessage(error, m => t(`student_feedback.${m}`))
      } else {
        onError(t('api.default_error_message'))
      }

      setLoading(false)
    })
  }

  useEffect(() => {
    loadEvaluation(user, internship)
  }, [loadEvaluation])

  const questions = useMemo(() => evaluationQuestions.map(q => {
    const label = q.questions.find(qq => qq.language === language)?.value ?? 'MISSING_LABEL'
    let answer = null

    if (evaluation?.studentEvaluationAnswers) {
      answer = evaluation.studentEvaluationAnswers.find(a => a.question === q.id)?.answer ?? null
    }

    return { id: q.id, label, answer }
  }), [evaluationQuestions, evaluation, language])

  const studentName = useMemo(() => {
    let name = t('Unknown student')

    if (internship?.student && internship.student.firstname) {
      name = internship.student.firstname
    }

    return name
  }, [internship?.student, t])

  return (
    <Loading loading={loading}>
      <EvaluationPanel
        comment={evaluation?.studentEvaluationComment ?? ''}
        questions={questions}
        name={studentName}
        submitted={submitted}
        onSubmit={submitFeedback}
      />
    </Loading>
  )
}

export default connect(mapStateToProps)(InstitutionEvaluationPanel)
